// app/store/authSlice.ts
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { api } from './api';
import { RootState } from './store';
import { User } from '../interfaces/user';

interface AuthState {
  token?: string | null;
  user?: User;
  authenticating?: boolean;
}

// Load token from localStorage (client-side only)
const loadToken = () => {
  // Check if the code is running in the browser
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem('token');
    if (token) {
      console.log('Loaded token from localStorage:', token.substring(0, 20) + '...');
    }
    return token;
  }
  // Return null if not in a browser environment
  return null;
};

const initialState: AuthState = {
  token: null, // Will be loaded in useEffect to avoid hydration mismatch
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken(state, action: PayloadAction<string | null>) {
      state.token = action.payload;
      // localStorage operations moved to thunks to avoid hydration issues
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
    },
    logout(state) {
      state.token = null;
      state.user = undefined;
      // localStorage operations moved to thunks to avoid hydration issues
    },
  },
  extraReducers: (builder) => {
    // Handle loadTokenFromStorage thunk - addCase must come before addMatcher
    builder.addCase(loadTokenFromStorage.fulfilled, (state, action) => {
      if (action.payload) {
        state.token = action.payload;
      }
    });
    
    builder.addMatcher(
      (action: any) => action.error && (action.error.name === 'NotAuthenticated' || action.error.message?.includes('invalid signature')),
      (state, action: any) => {
        console.log('Authentication error detected, clearing auth state:', action.error);
        state.authenticating = false;
        authSlice.caseReducers.logout(state);
      }
    );
    
    // set authenticating to true when the authenticate.pending action is dispatched
    builder.addMatcher(
      api.endpoints.authenticate.matchPending,
      (state) => {
        state.authenticating = true;
      }
    );
    // set authenticating to false and set the token when the authenticate.fulfilled action is dispatched
    builder.addMatcher(
      api.endpoints.authenticate.matchFulfilled,
      (state, { payload }) => {
        state.authenticating = false;
        const action = authSlice.actions.setToken(payload.accessToken);
        authSlice.caseReducers.setToken(state, action);

        const setUserAction = authSlice.actions.setUser(payload.user);
        authSlice.caseReducers.setUser(state, setUserAction);
        
        // Save to localStorage asynchronously
        if (typeof window !== 'undefined') {
          localStorage.setItem('token', payload.accessToken);
        }
      }
    );
    // set authenticating to false and clear the token when the authenticate.rejected action is dispatched
    builder.addMatcher(
      api.endpoints.authenticate.matchRejected,
      (state) => {
        state.authenticating = false;
        authSlice.caseReducers.setToken(state, authSlice.actions.setToken(null));
      }
    );
  },
});

// isAuthenticated selector using redux toolkit createSelector
export const isAuthenticatedSelector = createSelector(
  (state: AuthState) => state.token,
  (token) => !!token
);

// Create a logout thunk
export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    dispatch(authSlice.actions.logout());
    // Clear localStorage on client side
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
    }
  }
);

export const loadTokenFromStorage = createAsyncThunk(
  'auth/loadTokenFromStorage',
  async () => {
    // Load token from localStorage on client side only
    return loadToken();
  }
);

export const setTokenThunk = createAsyncThunk(
  'auth/setTokenThunk',
  async (token: string | null, { dispatch }) => {
    dispatch(authSlice.actions.setToken(token));
    // Update localStorage on client side
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
    }
  }
);

export const isPublisherSelector = createSelector(
  (state: RootState) => state.auth.user,
  (user) => {
      if (user?.type === 'publisher') {
          return true;
      }

      return false;
  }
)

export const isAdminSelector = createSelector(
  (state: RootState) => state.auth.user,
  (user) => {
      if (user?.type === 'admin' || user?.type === 'superadmin') {
          return true;
      }

      return false;
  }
)

export const initAuth = createAsyncThunk<void, void, { state: RootState }>(
  "initAuth",
  async (_, thunkApi) => {
    const accessToken = loadToken();

    // Only attempt authentication if we have a token
    if (accessToken) {
      try {
        await thunkApi.dispatch(
          api.endpoints.authenticate.initiate({
            strategy: "jwt",
            accessToken,
          })
        );
      } catch (error) {
        // If JWT authentication fails, clear the invalid token
        console.warn('JWT authentication failed, clearing invalid token');
        thunkApi.dispatch(authSlice.actions.logout());
      }
    }
  }
);

// Helper function to clear all auth data (for debugging)
export const clearAllAuthData = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('token');
    console.log('Cleared all auth data from localStorage');
  }
};

// Make clearAllAuthData available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearAllAuthData = clearAllAuthData;
}

export const { setToken, logout: clearToken, logout } = authSlice.actions;
export default authSlice.reducer;
